import type { IconWeight } from '@phosphor-icons/react'
import type { ComponentType } from 'react'
import type { NavItemType } from 'types/Sidebar'

/** A phosphor icon, or anything drawn the same way (the AI sparkle for AI Agent). */
export type RailIcon = ComponentType<{ size?: number | string; weight?: IconWeight; className?: string }>

export type RailItem = { key: string; label: string; href: string; active: boolean; Icon: RailIcon }

export type RailGroup = { id: string; label: string; items: RailItem[] }

/**
 * A page that is one grouped-sidebar row with its sections in a rail inside the page well
 * (Settings, Ops). The rail never keeps its own list: it groups the row's own children after the
 * sidebar's visibility filter, so a section shows exactly when the old expanding group showed it.
 */
export type RailConfig = {
  /** The grouped footer row whose children are the rail's sections. */
  rowName: string
  /** The heading of the phone list and the label of a section's back link to it. */
  title: string
  /** Where a phone shows the grouped list; on desktop that route redirects to a section. */
  homePath: string
  /** The only paths a section can live on. Everything else never reads the sections. */
  routes: RegExp[]
  groups: { id: string; label: string }[]
  /**
   * Each child, by its name, to its group and icon. Keyed by name because the name is what the
   * sidebar item owns; a child missing here lands in `fallbackGroup` and fails its rail's spec.
   */
  sections: Record<string, { group: string; Icon: RailIcon; label?: string }>
  fallbackGroup: string
  fallbackIcon: RailIcon
}

/**
 * What a section is called on screen. The rail and the browser tab both read it, so a section
 * never has two names.
 */
export function railSectionLabel(rail: RailConfig, child: NavItemType): string | undefined {
  return (child.name && rail.sections[child.name]?.label) ?? child.name
}

/** The row's (already filtered) children as rail groups, in rail order; empty groups are dropped. */
export function groupRailSections(children: NavItemType[], rail: RailConfig): RailGroup[] {
  const items = children.flatMap((child): (RailItem & { group: string })[] => {
    // Dividers and anything else without a page of its own have no row in a rail.
    if (!child.name || !child.href) return []
    const section = rail.sections[child.name]
    return [
      {
        key: child.href,
        label: railSectionLabel(rail, child) ?? child.name,
        href: child.href,
        active: !!child.active,
        Icon: section?.Icon ?? rail.fallbackIcon,
        group: section?.group ?? rail.fallbackGroup,
      },
    ]
  })
  return rail.groups.flatMap((group) => {
    const groupItems = items
      .filter((item) => item.group === group.id)
      .map((item) => {
        const { group: _group, ...railItem } = item
        return railItem
      })
    return groupItems.length > 0 ? [{ ...group, items: groupItems }] : []
  })
}
